import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Layout5Root } from './Layout5Root';
import { LayoutHeader as Header } from '@/layouts';
import { LayoutFooter as Footer } from '@/layouts';
import { VehicleListCard } from './VehicleListCard';
import { VehicleFilters } from '@/components/VehicleFilters';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { SlidersHorizontal, X, ArrowUpDown, Check } from 'lucide-react';
import { useAppData, useFilteredVehicles } from '@/contexts/AppDataContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useStore } from '@/contexts/StoreContext';

const VehiclesListing = () => {
  const { config } = useAppData();
  const { colors } = useTheme();
  const { selectedStore } = useStore();
  const store = selectedStore || config?.stores?.[0];
  const contextVehicles = useFilteredVehicles(selectedStore);
  const allStores = config?.stores || [];
  const buttonColor = colors.button;
  const primaryColor = colors.primary;
  const [searchParams, setSearchParams] = useSearchParams();
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [sortBy, setSortBy] = useState('price-asc');
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    brand: true,
    year: true,
    price: true,
    mileage: true,
    fuel: true,
    category: true,
    transmission: true,
    color: true,
    optionals: true,
    type: true,
    store: true
  });

  // Infinite scroll states
  const [displayedCount, setDisplayedCount] = useState(12);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const itemsPerLoad = 12;
  const maxDisplayedItems = 100; // Limit infinite scroll to 100 items
  const observerTarget = useRef<HTMLDivElement>(null);

  // Calculate dynamic ranges from contextVehicles using useMemo
  const ranges = useMemo(() => {
    if (contextVehicles.length === 0) {
      return {
        minPrice: 0,
        maxPrice: 500000,
        minYear: 2000,
        maxYear: 2024,
        minMileage: 0,
        maxMileage: 200000
      };
    }

    const minModelYear = Math.min(...contextVehicles.map(v => v.modelYear));
    const maxModelYear = Math.max(...contextVehicles.map(v => v.modelYear));

    return {
      minPrice: Math.min(...contextVehicles.map(v => v.price)),
      maxPrice: Math.max(...contextVehicles.map(v => v.price)),
      minYear: minModelYear - 1, // Include previous year (e.g., 2024 if minModelYear is 2025)
      maxYear: maxModelYear,
      minMileage: Math.min(...contextVehicles.map(v => v.mileage)),
      maxMileage: Math.max(...contextVehicles.map(v => v.mileage))
    };
  }, [contextVehicles]);

  // Filter states
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [selectedBrand, setSelectedBrand] = useState(searchParams.get('brand') || 'all');
  const [selectedModel, setSelectedModel] = useState(searchParams.get('model') || 'all');
  const [selectedFuels, setSelectedFuels] = useState<string[]>([]);
  const [selectedTransmissions, setSelectedTransmissions] = useState<string[]>([]);
  const [selectedType, setSelectedType] = useState(searchParams.get('type') || 'all');
  const [isArmoredFilter, setIsArmoredFilter] = useState(searchParams.get('armored') === 'true');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'all');
  const [selectedColors, setSelectedColors] = useState<string[]>([]);
  const [selectedOptionals, setSelectedOptionals] = useState<string[]>([]);
  const [selectedStores, setSelectedStores] = useState<string[]>([]);
  const [priceRange, setPriceRange] = useState([ranges.minPrice, ranges.maxPrice]);
  const [yearRange, setYearRange] = useState([ranges.minYear, ranges.maxYear]);
  const [mileageRange, setMileageRange] = useState([ranges.minMileage, ranges.maxMileage]);

  // Get unique filter options using useMemo
  const brands = useMemo(() => Array.from(new Set(contextVehicles.map(v => v.brand))).sort(), [contextVehicles]);
  const models = useMemo(() => selectedBrand === 'all'
    ? Array.from(new Set(contextVehicles.map(v => v.model))).sort()
    : Array.from(new Set(contextVehicles.filter(v => v.brand === selectedBrand).map(v => v.model))).sort(),
    [contextVehicles, selectedBrand]
  );
  const fuels = useMemo(() => Array.from(new Set(contextVehicles.map(v => v.fuel))).sort(), [contextVehicles]);
  const transmissions = useMemo(() => Array.from(new Set(contextVehicles.map(v => v.transmission))).sort(), [contextVehicles]);
  const vehicleColors = useMemo(() => Array.from(new Set(contextVehicles.map(v => v.color))).sort(), [contextVehicles]);

  // Get unique optionals from all vehicles
  const optionals = useMemo(() => {
    const allOptionals = contextVehicles.flatMap(v => v.optionals || []);
    return Array.from(new Set(allOptionals)).sort();
  }, [contextVehicles]);

  // Get unique categories (1 = Carro, 2 = Moto, 3 = Caminhão)
  const categories = useMemo(() => {
    const uniqueTypes = Array.from(new Set(contextVehicles.map(v => v.type)));
    return uniqueTypes.map(type => {
      switch (type) {
        case 1: return { value: '1', label: 'Carro' };
        case 2: return { value: '2', label: 'Moto' };
        case 3: return { value: '3', label: 'Caminhão' };
        default: return null;
      }
    }).filter(Boolean).sort((a, b) => a!.value.localeCompare(b!.value)) as { value: string, label: string }[];
  }, [contextVehicles]);

  // Update URL params when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (searchTerm && searchTerm.trim() !== '') params.set('search', searchTerm);
    if (selectedBrand !== 'all') params.set('brand', selectedBrand);
    if (selectedModel !== 'all') params.set('model', selectedModel);
    if (selectedType !== 'all') params.set('type', selectedType);
    if (selectedCategory !== 'all') params.set('category', selectedCategory);
    if (isArmoredFilter) params.set('armored', 'true');

    // Se não há parâmetros, usar URL limpa
    const newUrl = params.toString() ? `?${params.toString()}` : '';
    setSearchParams(newUrl);
  }, [searchTerm, selectedBrand, selectedModel, selectedType, selectedCategory, isArmoredFilter]);

  // Initialize range sliders when ranges change (only on mount or when contextVehicles change significantly)
  useEffect(() => {
    setPriceRange([ranges.minPrice, ranges.maxPrice]);
    setYearRange([ranges.minYear, ranges.maxYear]);
    setMileageRange([ranges.minMileage, ranges.maxMileage]);
  }, [ranges.minPrice, ranges.maxPrice, ranges.minYear, ranges.maxYear, ranges.minMileage, ranges.maxMileage]);

  // Initialize filters from URL params on mount
  useEffect(() => {
    const typeParam = searchParams.get('type');
    const armoredParam = searchParams.get('armored');

    if (typeParam === 'zerokm' || typeParam === 'seminovo') {
      setSelectedType(typeParam);
      setIsArmoredFilter(false);
    } else if (armoredParam === 'true') {
      setIsArmoredFilter(true);
      setSelectedType('all');
    } else {
      setSelectedType(typeParam || 'all');
      setIsArmoredFilter(false);
    }

    const searchParam = searchParams.get('search');
    if (searchParam) {
      setSearchTerm(searchParam);
    } else {
      setSearchTerm('');
    }

    const brandParam = searchParams.get('brand');
    if (brandParam) {
      setSelectedBrand(brandParam);
    } else {
      setSelectedBrand('all');
    }

    const modelParam = searchParams.get('model');
    if (modelParam) {
      setSelectedModel(modelParam);
    } else {
      setSelectedModel('all');
    }

    const categoryParam = searchParams.get('category');
    if (categoryParam) {
      setSelectedCategory(categoryParam);
    } else {
      setSelectedCategory('all');
    }

    // Initialize multi-select filters from URL params (single value)
    const fuelParam = searchParams.get('fuel');
    if (fuelParam) {
      setSelectedFuels([fuelParam]);
    }

    const transmissionParam = searchParams.get('transmission');
    if (transmissionParam) {
      setSelectedTransmissions([transmissionParam]);
    }

    const colorParam = searchParams.get('color');
    if (colorParam) {
      setSelectedColors([colorParam]);
    }

    const storeParam = searchParams.get('store');
    if (storeParam) {
      setSelectedStores([storeParam]);
    }

    // Initialize year range from URL params
    const minYearParam = searchParams.get('minYear');
    const maxYearParam = searchParams.get('maxYear');
    if (minYearParam && maxYearParam) {
      const minYear = parseInt(minYearParam);
      const maxYear = parseInt(maxYearParam);
      if (!isNaN(minYear) && !isNaN(maxYear)) {
        setYearRange([minYear, maxYear]);
      }
    }
  }, [searchParams]);

  // Filter and sort vehicles using useMemo
  const filteredVehicles = useMemo(() => {
    // Check if filtering by specific IDs (from banner)
    const idsParam = searchParams.get('ids');
    let filtered = [...contextVehicles];

    if (idsParam) {
      // Filter by specific vehicle IDs
      const vehicleIds = idsParam.split(',');
      filtered = contextVehicles.filter(vehicle => vehicleIds.includes(vehicle.id.toString()));
    } else {
      // Get URL params for multi-select filters
      const urlFuel = searchParams.get('fuel');
      const urlTransmission = searchParams.get('transmission');
      const urlColor = searchParams.get('color');
      
      // Use URL params if available, otherwise use state
      const activeFuels = urlFuel ? [urlFuel] : selectedFuels;
      const activeTransmissions = urlTransmission ? [urlTransmission] : selectedTransmissions;
      const activeColors = urlColor ? [urlColor] : selectedColors;
      
      // Apply regular filters
      filtered = contextVehicles.filter(vehicle => {
        const matchesSearch = searchTerm === '' ||
          `${vehicle.brand} ${vehicle.model} ${vehicle.version}`
            .toLowerCase()
            .includes(searchTerm.toLowerCase());

        const matchesBrand = selectedBrand === 'all' || vehicle.brand === selectedBrand;
        const matchesModel = selectedModel === 'all' || vehicle.model === selectedModel;
        const matchesFuels = activeFuels.length === 0 || activeFuels.includes(vehicle.fuel);
        const matchesTransmissions = activeTransmissions.length === 0 || activeTransmissions.includes(vehicle.transmission);
        const matchesType = selectedType === 'all' ||
          (selectedType === 'zerokm' && (vehicle.isZeroKm || vehicle.mileage == 0)) ||
          (selectedType === 'seminovo' && !vehicle.isZeroKm && vehicle.mileage > 0 && !vehicle.armored);
        const matchesArmored = !isArmoredFilter || vehicle.armored;
        const matchesCategory = selectedCategory === 'all' || vehicle.type.toString() === selectedCategory;
        const matchesColors = activeColors.length === 0 || activeColors.includes(vehicle.color);
        const matchesOptionals = selectedOptionals.length === 0 ||
          selectedOptionals.every(optional => vehicle.optionals?.includes(optional));
        const matchesStores = selectedStores.length === 0 || (vehicle.store && selectedStores.includes(vehicle.store));
        const matchesPrice = vehicle.price >= priceRange[0] && vehicle.price <= priceRange[1];
        // Year filter: if same year, filter by exact modelYear; otherwise use range
        const matchesYear = yearRange[0] === yearRange[1]
          ? vehicle.modelYear === yearRange[0]
          : vehicle.manufactureYear >= yearRange[0] && vehicle.modelYear <= yearRange[1];
        const matchesMileage = vehicle.mileage >= mileageRange[0] && vehicle.mileage <= mileageRange[1];

        return matchesSearch && matchesBrand && matchesModel && matchesFuels &&
          matchesTransmissions && matchesType && matchesArmored && matchesCategory && matchesColors &&
          matchesOptionals && matchesStores && matchesPrice && matchesYear && matchesMileage;
      });
    }

    // Sort vehicles
    const sorted = [...filtered];
    switch (sortBy) {
      case 'price-asc':
        sorted.sort((a, b) => a.price - b.price);
        break;
      case 'price-desc':
        sorted.sort((a, b) => b.price - a.price);
        break;
      case 'year-desc':
        sorted.sort((a, b) => b.modelYear - a.modelYear);
        break;
      case 'year-asc':
        sorted.sort((a, b) => a.modelYear - b.modelYear);
        break;
      case 'mileage-asc':
        sorted.sort((a, b) => a.mileage - b.mileage);
        break;
      case 'brand':
        sorted.sort((a, b) => a.brand.localeCompare(b.brand));
        break;
      default:
        break;
    }

    return sorted;
  }, [contextVehicles, searchTerm, selectedBrand, selectedModel, selectedFuels, selectedTransmissions,
    selectedType, selectedCategory, selectedColors, selectedOptionals, selectedStores, isArmoredFilter, priceRange, yearRange, mileageRange, sortBy, searchParams]);

  // Reset displayed count when filters change
  useEffect(() => {
    setDisplayedCount(12);
    setIsLoadingMore(false);
  }, [filteredVehicles]);

  // Load more function
  const loadMore = useCallback(() => {
    if (isLoadingMore) return;
    
    setIsLoadingMore(true);
    setDisplayedCount(prev => Math.min(prev + itemsPerLoad, filteredVehicles.length, maxDisplayedItems));
    // Use requestAnimationFrame to ensure state update before releasing lock
    requestAnimationFrame(() => {
      setIsLoadingMore(false);
    });
  }, [isLoadingMore, filteredVehicles.length, maxDisplayedItems]);

  // Infinite scroll observer
  useEffect(() => {
    const currentTarget = observerTarget.current;
    
    if (!currentTarget) return;
    
    // Don't observe if we've reached the limit
    if (displayedCount >= Math.min(filteredVehicles.length, maxDisplayedItems)) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMore();
        }
      },
      { threshold: 0.1, rootMargin: '100px' }
    );

    observer.observe(currentTarget);

    return () => {
      observer.unobserve(currentTarget);
    };
  }, [displayedCount, filteredVehicles.length, maxDisplayedItems, loadMore]);

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedBrand('all');
    setSelectedModel('all');
    setSelectedFuels([]);
    setSelectedTransmissions([]);
    setSelectedType('all');
    setSelectedCategory('all');
    setIsArmoredFilter(false);
    setSelectedColors([]);
    setSelectedOptionals([]);
    setSelectedStores([]);
    setPriceRange([ranges.minPrice, ranges.maxPrice]);
    setYearRange([ranges.minYear, ranges.maxYear]);
    setMileageRange([ranges.minMileage, ranges.maxMileage]);
    setSearchParams(new URLSearchParams());
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price);
  };

  const hasActiveFilters = searchTerm || selectedBrand !== 'all' || selectedModel !== 'all' ||
    selectedFuels.length > 0 || selectedTransmissions.length > 0 ||
    selectedType !== 'all' || selectedCategory !== 'all' || isArmoredFilter || selectedColors.length > 0 ||
    selectedOptionals.length > 0 || selectedStores.length > 0 ||
    priceRange[0] > ranges.minPrice || priceRange[1] < ranges.maxPrice ||
    yearRange[0] > ranges.minYear || yearRange[1] < ranges.maxYear ||
    mileageRange[0] > ranges.minMileage || mileageRange[1] < ranges.maxMileage;

  // Get vehicles to display (infinite scroll)
  const displayedVehicles = filteredVehicles.slice(0, displayedCount);

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  return (
    <Layout5Root withHeader className="bg-background">
      <Header config={config} />

      <section className="pb-8 pt-4 lg:pt-6">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">

            {/* Page Title */}
            {/* <div className="mb-6">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
                Estoque de Veículos
              </h1>
              <p className="text-sm text-muted-foreground">
                Encontre o veículo ideal para você
              </p>
            </div> */}

            {/* Mobile Filter Button */}
            <div className="mb-6 lg:hidden">
              <Sheet open={showMobileFilters} onOpenChange={setShowMobileFilters}>
                <SheetTrigger asChild>
                  <Button variant="outline" className="w-full h-12">
                    <SlidersHorizontal className="h-5 w-5 mr-2" />
                    Filtros
                    {hasActiveFilters && <Badge variant="secondary" className="ml-2">!</Badge>}
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-[100%] p-0 flex flex-col gap-0">
                  <SheetHeader className="sticky top-0 z-10 bg-background border-b px-6 py-4 m-0">
                    <div className="flex items-center justify-between">
                      <div>
                        <SheetTitle>Filtros</SheetTitle>
                        <p className="text-xs text-muted-foreground mt-1">
                          {filteredVehicles.length} {filteredVehicles.length === 1 ? 'veículo' : 'veículos'}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => setShowMobileFilters(false)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </SheetHeader>
                  <div className="flex-1 overflow-y-auto px-6 py-4">
                    <VehicleFilters
                      searchTerm={searchTerm}
                      setSearchTerm={setSearchTerm}
                      selectedBrand={selectedBrand}
                      setSelectedBrand={setSelectedBrand}
                      selectedModel={selectedModel}
                      setSelectedModel={setSelectedModel}
                      selectedFuels={selectedFuels}
                      setSelectedFuels={setSelectedFuels}
                      selectedTransmissions={selectedTransmissions}
                      setSelectedTransmissions={setSelectedTransmissions}
                      selectedType={selectedType}
                      setSelectedType={setSelectedType}
                      isArmoredFilter={isArmoredFilter}
                      setIsArmoredFilter={setIsArmoredFilter}
                      selectedCategory={selectedCategory}
                      setSelectedCategory={setSelectedCategory}
                      selectedColors={selectedColors}
                      setSelectedColors={setSelectedColors}
                      selectedOptionals={selectedOptionals}
                      setSelectedOptionals={setSelectedOptionals}
                      selectedStores={selectedStores}
                      setSelectedStores={setSelectedStores}
                      priceRange={priceRange}
                      setPriceRange={setPriceRange}
                      yearRange={yearRange}
                      setYearRange={setYearRange}
                      mileageRange={mileageRange}
                      setMileageRange={setMileageRange}
                      config={config}
                      brands={brands}
                      models={models}
                      fuels={fuels}
                      transmissions={transmissions}
                      categories={categories}
                      colors={vehicleColors}
                      optionals={optionals}
                      availableStores={allStores}
                      selectedStore={selectedStore}
                      vehicles={contextVehicles}
                      ranges={ranges}
                      clearFilters={clearFilters}
                      hasActiveFilters={hasActiveFilters}
                      filteredCount={filteredVehicles.length}
                      expandedSections={expandedSections}
                      toggleSection={toggleSection}
                      hideHeader={true}
                    />
                  </div>

                  {/* Footer - Fixed */}
                  <div className="sticky bottom-0 z-10 bg-background border-t px-6 py-4">
                    <div className="flex gap-3">
                      <Button
                        variant="outline"
                        className="flex-1"
                        onClick={clearFilters}
                        disabled={!hasActiveFilters}
                      >
                        Limpar
                      </Button>
                      <Button
                        className="flex-1 text-white font-semibold"
                        style={{
                          background: `linear-gradient(to right, ${buttonColor}, ${buttonColor}dd)`,
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = `linear-gradient(to right, ${buttonColor}ee, ${buttonColor})`;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = `linear-gradient(to right, ${buttonColor}, ${buttonColor}dd)`;
                        }}
                        onClick={() => setShowMobileFilters(false)}
                      >
                        <Check className="h-4 w-4 mr-2" />
                        Aplicar Filtros
                      </Button>
                    </div>
                  </div>
                </SheetContent>
              </Sheet>
            </div>

            {/* Content + Filters (filtros à direita — exclusivo layout 5) */}
            <div className="wl5-listing-layout grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">

              {/* Main Content Area */}
              <div className="min-w-0">
                {/* Results Header - Hidden on Mobile */}
                <div className="hidden lg:flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6 pb-4 border-b">
                  <div>
                    <h2 className="text-base font-semibold text-foreground">
                      {filteredVehicles.length} {filteredVehicles.length === 1 ? 'veículo encontrado' : 'veículos encontrados'}
                    </h2>
                    {hasActiveFilters && (
                      <p className="text-xs text-muted-foreground">
                        Com filtros aplicados
                      </p>
                    )}
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    <Select value={sortBy} onValueChange={setSortBy}>
                      <SelectTrigger className="w-[200px] h-10">
                        <ArrowUpDown className="h-4 w-4 mr-2" />
                        <SelectValue placeholder="Ordenar por" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="price-asc">Menor preço</SelectItem>
                        <SelectItem value="price-desc">Maior preço</SelectItem>
                        <SelectItem value="year-desc">Mais novo</SelectItem>
                        <SelectItem value="year-asc">Mais antigo</SelectItem>
                        <SelectItem value="mileage-asc">Menor quilometragem</SelectItem>
                        <SelectItem value="brand">Marca (A-Z)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Sort — mobile */}
                <div className="lg:hidden mb-6">
                  <Select value={sortBy} onValueChange={setSortBy}>
                    <SelectTrigger className="w-full h-12">
                      <ArrowUpDown className="h-4 w-4 mr-2" />
                      <SelectValue placeholder="Ordenar por" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="price-asc">Menor preço</SelectItem>
                      <SelectItem value="price-desc">Maior preço</SelectItem>
                      <SelectItem value="year-desc">Mais novo</SelectItem>
                      <SelectItem value="year-asc">Mais antigo</SelectItem>
                      <SelectItem value="mileage-asc">Menor quilometragem</SelectItem>
                      <SelectItem value="brand">Marca (A-Z)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Vehicles Grid */}
                {filteredVehicles.length > 0 ? (
                  <>
                    <div className="wl5-vehicle-list">
                      {displayedVehicles.map((vehicle) => (
                        <VehicleListCard
                          key={vehicle.id}
                          vehicle={vehicle}
                          whatsappUrl={store?.whatsapp?.ios}
                        />
                      ))}
                    </div>

                    {/* Loading indicator / Observer target */}
                    {displayedCount < Math.min(filteredVehicles.length, maxDisplayedItems) && (
                      <div ref={observerTarget} className="mt-8 flex justify-center py-8">
                        <div className="flex flex-col items-center gap-3">
                          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"
                            style={{ borderColor: `${primaryColor} transparent ${primaryColor} ${primaryColor}` }}
                          />
                          <p className="text-sm text-muted-foreground">
                            Carregando mais veículos... ({displayedCount} de {Math.min(filteredVehicles.length, maxDisplayedItems)})
                          </p>
                        </div>
                      </div>
                    )}
                    
                    {/* End of list indicator */}
                    {displayedCount >= maxDisplayedItems && filteredVehicles.length > maxDisplayedItems ? (
                      <div className="mt-8 flex flex-col items-center gap-4 py-6">
                        <p className="text-sm text-muted-foreground">
                          Mostrando {maxDisplayedItems} de {filteredVehicles.length} veículos
                        </p>
                        <p className="text-xs text-muted-foreground max-w-md text-center">
                          Use os filtros para refinar sua busca e ver resultados mais específicos
                        </p>
                      </div>
                    ) : displayedCount >= filteredVehicles.length && filteredVehicles.length > 12 ? (
                      <div className="mt-8 flex justify-center py-6">
                        <p className="text-sm text-muted-foreground">
                          Todos os {filteredVehicles.length} veículos foram carregados
                        </p>
                      </div>
                    ) : null}
                  </>
                ) : (
                  <div className="text-center py-16">
                    <h3 className="text-2xl font-bold text-foreground mb-4">
                      Nenhum veículo encontrado
                    </h3>
                    <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                      Não encontramos veículos com os filtros selecionados.<br />Tente ajustar sua busca.
                    </p>
                    <Button onClick={clearFilters} style={{ background: primaryColor }}>
                      Limpar Filtros
                    </Button>
                  </div>
                )}
              </div>

              {/* Sidebar Filters - Desktop (direita) */}
              <aside className="hidden lg:block min-w-0">
                <Card className="wl5-listing-filters sticky top-[calc(var(--wl5-header-h,4rem)+0.75rem)] shadow-sm border-border/50 mb-6">
                  <CardContent className="p-5 pb-0 h-[calc(100vh-var(--wl5-header-h,4rem)-2rem)] flex flex-col">
                    <VehicleFilters
                      searchTerm={searchTerm}
                      setSearchTerm={setSearchTerm}
                      selectedBrand={selectedBrand}
                      setSelectedBrand={setSelectedBrand}
                      selectedModel={selectedModel}
                      setSelectedModel={setSelectedModel}
                      selectedFuels={selectedFuels}
                      setSelectedFuels={setSelectedFuels}
                      selectedTransmissions={selectedTransmissions}
                      setSelectedTransmissions={setSelectedTransmissions}
                      selectedType={selectedType}
                      setSelectedType={setSelectedType}
                      isArmoredFilter={isArmoredFilter}
                      setIsArmoredFilter={setIsArmoredFilter}
                      selectedCategory={selectedCategory}
                      setSelectedCategory={setSelectedCategory}
                      selectedColors={selectedColors}
                      setSelectedColors={setSelectedColors}
                      selectedOptionals={selectedOptionals}
                      setSelectedOptionals={setSelectedOptionals}
                      selectedStores={selectedStores}
                      setSelectedStores={setSelectedStores}
                      priceRange={priceRange}
                      setPriceRange={setPriceRange}
                      yearRange={yearRange}
                      setYearRange={setYearRange}
                      mileageRange={mileageRange}
                      setMileageRange={setMileageRange}
                      config={config}
                      brands={brands}
                      models={models}
                      fuels={fuels}
                      transmissions={transmissions}
                      categories={categories}
                      colors={vehicleColors}
                      optionals={optionals}
                      availableStores={allStores}
                      selectedStore={selectedStore}
                      vehicles={contextVehicles}
                      ranges={ranges}
                      clearFilters={clearFilters}
                      hasActiveFilters={hasActiveFilters}
                      filteredCount={filteredVehicles.length}
                      expandedSections={expandedSections}
                      toggleSection={toggleSection}
                    />
                  </CardContent>
                </Card>
              </aside>
            </div>
          </div>
        </div>
      </section>

      <Footer config={config} />
    </Layout5Root>
  );
};

export default VehiclesListing;
